import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, Edit2, X, Check, MessageSquare, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });
const emptyForm = { quote: "", name: "", exam: "" };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/testimonials`);
      setItems(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditItem(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (item) => { setEditItem(item); setForm({ quote: item.quote, name: item.name, exam: item.exam }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.quote || !form.name) { alert("Quote and Name are required."); return; }
    setSaving(true);
    try {
      if (editItem) {
        const res = await axios.put(`${BACKEND_URL}/api/admin/testimonials/${editItem.id}`, form, { headers: getHeaders() });
        setItems((prev) => prev.map((i) => (i.id === editItem.id ? res.data : i)));
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/admin/testimonials`, form, { headers: getHeaders() });
        setItems((prev) => [...prev, res.data]);
      }
      setShowModal(false);
    } catch { alert("Failed to save."); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/testimonials/${id}`, { headers: getHeaders() });
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch { alert("Failed to delete."); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

  return (
    <div data-testid="admin-testimonials-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-extrabold" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>Testimonials Management</h2>
          <p className="text-sm text-gray-400 mt-0.5">{items.length} testimonials on website</p>
        </div>
        <button onClick={openAdd} data-testid="testimonial-add-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90"
          style={{ backgroundColor: "#0A2540" }}>
          <Plus size={16} /> Add Testimonial
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
          <MessageSquare size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">No testimonials yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <div key={item.id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm border-l-4"
              style={{ borderLeftColor: "#F2C94C" }} data-testid={`testimonial-card-admin-${i}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, j) => <Star key={j} size={12} fill="#F2C94C" color="#F2C94C" />)}
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500">
                    <Edit2 size={13} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4 italic line-clamp-4">"{item.quote}"</p>
              <div>
                <div className="font-bold text-sm" style={{ color: "#0A2540" }}>{item.name}</div>
                <div className="text-xs text-gray-400 mt-0.5">{item.exam}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>
                {editItem ? "Edit Testimonial" : "Add Testimonial"}
              </h3>
              <button onClick={() => setShowModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide">Student Quote *</label>
                <textarea value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })}
                  rows={5} placeholder="Write the student's testimonial..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900 resize-none" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide">Student Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Student name"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide">Exam / Achievement</label>
                <input type="text" value={form.exam} onChange={(e) => setForm({ ...form, exam: e.target.value })}
                  placeholder="e.g. NEET 572 | MBBS GMC Alibag"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving} data-testid="testimonial-save-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: "#0A2540" }}>
                <Check size={14} />{saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
